import { ChangeDetectionStrategy, Component, Input, OnInit, HostListener, ElementRef, ChangeDetectorRef, OnDestroy, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CrosstabService } from 'src/app/common/services/crosstab.service';
import { SignificanceService } from 'src/app/common/services/significance.service';
import { CrossTabOutputItem, PairWiseDetails } from 'src/app/common/models/crosstab.model';
import * as _ from 'lodash';
import { COMMON_CONSTANTS } from '../../constants/common-constants';
import { AuthenticationService } from 'src/app/common/services/authentication.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';

@Component({
  selector: 'app-table-ouput',
  templateUrl: './table-ouput.component.html',
  styleUrls: ['./table-ouput.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush // if any change occure then the component will get call.Optimize change detection for better performance
})
export class TableOuputComponent implements OnInit, OnDestroy, OnChanges {
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | 'ascd' = 'ascd';
  private tableSortingSubscription: any;
  private sortingInProgress: boolean = false;
  private originalRowData: any[] | null = null;

  @Input() isPairWise: boolean = false;
  @Input() DefaultConfidenceLevel: string | undefined = '95%';
  @Input() insufficientBase: string = '';
  @Input() LowBase: string = '';
  @Input() selectedIndex: string = '';
  @Input() isIndex: boolean = false;
  @Input() isBrandAnalysis: boolean = false;
  @Input() isSampleSizeBlock: boolean = false;
  @Input() isWeightedSampleBlock: boolean = false;
  @Input() selectedWeight: string = '';
  @Input() selectedDataFormat: string = '';
  LowBaseSample = COMMON_CONSTANTS.LowBase_Sample;
  InsufficientBaseSample = COMMON_CONSTANTS.InsufficientBase_Sample;
  @Input() threshold: string = '';
  ThresholdValue: number = 0;
  Sig_LowerLimt = COMMON_CONSTANTS.SigLowerLimit_95;
  Sig_UpperLimt = COMMON_CONSTANTS.SigUpperLimit_95;

  pairWiseVariables: string[] = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J","K","L"];
  @Input() data: any = [];
  @Input() decimalPlaces: number = 1;
  @Output() outputUpdated: EventEmitter<any> = new EventEmitter<any>();
  @Input() isTranspose: boolean = false;
  @Input() isFromAskMe: boolean = false;
  @Input() isLoading: boolean = false;
  cellWidth: number = 200;
  cellHeight: number = 45;
  crossTabReceivedData: any = [];
  @Input() isTotalBase: boolean = false;
  @Input() isAnswerBase: boolean = false;
  @Input() isSampleSize: boolean = false;
  @Input() isWeightedLabel: boolean = false;
  @Input() isWeightedNumerator: boolean = false;
  @Input() isUnWeightedNumerator: boolean = false;
  @Input() DefaultSampleSize: string | undefined;
  @Input() SignificanceVsReference: string = '';
  @Input() columnNestingSelections: any[] = [];
  @Input() columnNestingItems: any[] = [];
  isarkTabAccessed: any = 'false';
  private _crossTabReceivedData: any;
  constructor(
    private dataSharingService: CrosstabService,
    private _authenticateService: AuthenticationService,
    public breakpointObserver: BreakpointObserver,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private significanceService: SignificanceService
  ) { }

  ngOnInit() {
    if (this.isFromAskMe && !this.selectedDataFormat?.trim()) {
    this.selectedDataFormat = 'Respondent Level';
    }
    this.LowBaseSample = this.LowBase == "" ? 0 : Number(this.LowBase);
    this.InsufficientBaseSample = this.insufficientBase == "" ? 0 : Number(this.insufficientBase);
    this.ThresholdValue = this.threshold == "" ? 0 : Number(this.threshold);

    // Restore sort state from session storage
    const storedSortColumn = sessionStorage.getItem('tableSortColumn');
    const storedSortDirection = sessionStorage.getItem('tableSortDirection');
    if (storedSortColumn && storedSortDirection) {
      this.sortColumn = storedSortColumn;
      this.sortDirection = storedSortDirection as 'asc' | 'desc' | 'ascd';
    }

    this.getOutputData();
    this.dataSharingService.PairWiseColumnHeaderSubject$.subscribe((res)=>{
      this.pairWiseVariables=res;
    })
    if (this.isarkTabAccessed == 'false') {
      this.isarkTabAccessed = sessionStorage.getItem('isarkTabAccessed');
    }
    // Subscribe to global table-sorting events so this component reflects selected sort
    this.tableSortingSubscription = this.dataSharingService.tableSorting$.subscribe(({ sortColumn, sortDirection }: any) => {
      // update local state
      this.sortColumn = sortColumn || '';
      this.sortDirection = sortDirection || 'ascd';
      // Apply sorting to current data when sort state changes
      if (this.sortColumn && this.sortDirection !== 'ascd') {
        this.sortTableData();
      }
      // ensure OnPush components detect the change
      try { this.cdr.markForCheck(); } catch (e) { /* ignore */ }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.isFromAskMe) return;

    if (changes['selectedDataFormat'] && !this.selectedDataFormat?.trim()) {
      this.selectedDataFormat = 'Respondent Level';
    }

    if (changes['data']?.currentValue?.flatData) {
      this.crossTabReceivedData = changes['data'].currentValue.flatData;
    }
    
    if (changes['insufficientBase'] || changes['LowBase'] || changes['threshold']) {
    this.LowBaseSample = this.LowBase == "" ? 0 : Number(this.LowBase);
    this.InsufficientBaseSample = this.insufficientBase == "" ? 0 : Number(this.insufficientBase);
    this.ThresholdValue = this.threshold == "" ? 0 : Number(this.threshold);
  }
  }

  ngOnDestroy(): void {
    try {
      if (this.tableSortingSubscription && typeof this.tableSortingSubscription.unsubscribe === 'function') {
        this.tableSortingSubscription.unsubscribe();
      }
    } catch (e) {
      // ignore
    }
    // Clear session storage when component is destroyed to prevent stale sorting state
    this.clearSortState();
  }

  clearSortState() {
    sessionStorage.removeItem('tableSortColumn');
    sessionStorage.removeItem('tableSortDirection');
    this.sortColumn = '';
    this.sortDirection = 'ascd';
  }

  // Force re-apply current sorting (useful when data structure changes like significance)
  forceReSort() {
    if (this.sortColumn && this.sortDirection !== 'ascd') {
      this.sortTableData();
      this.dataSharingService.setTableSorting(this.sortColumn, this.sortDirection);
      this.cdr.markForCheck();
    }
  }

  // Public method to maintain sort state after data updates (significance, filters, etc.)
  maintainSortState() {
    // Use stored session values if current values are reset
    if (!this.sortColumn || this.sortDirection === 'ascd') {
      const storedSortColumn = sessionStorage.getItem('tableSortColumn');
      const storedSortDirection = sessionStorage.getItem('tableSortDirection');
      if (storedSortColumn && storedSortDirection && storedSortDirection !== 'ascd') {
        this.sortColumn = storedSortColumn;
        this.sortDirection = storedSortDirection as 'asc' | 'desc' | 'ascd';
      }
    }
    this.forceReSort();
  }
  onSortColumn(columnKey: string, direction: 'asc' | 'desc' | 'ascd') {
    // Toggle: clicking the same column+direction again resets to original order
    if (this.sortColumn === columnKey && this.sortDirection === direction) {
      this.resetSort();
      return;
    }
    this.sortColumn = columnKey;
    this.sortDirection = direction;
    this.sortTableData();
    // Notify charts about the sorting change
    this.dataSharingService.setTableSorting(columnKey, direction);
    // Store sort state to maintain it across data updates
    sessionStorage.setItem('tableSortColumn', columnKey);
    sessionStorage.setItem('tableSortDirection', direction);
  }

  resetSort() {
    if (this.originalRowData && this.data && this.data.Row && this.data.Row[0]) {
      const newRow = { ...this.data.Row[0], Data: _.cloneDeep(this.originalRowData) };
      this.data = { ...this.data, Row: [newRow] };
    }
    this.originalRowData = null;
    this.sortColumn = '';
    this.sortDirection = 'ascd';
    this.dataSharingService.setTableSorting('', 'ascd');
    sessionStorage.removeItem('tableSortColumn');
    sessionStorage.removeItem('tableSortDirection');
    setTimeout(() => {
      this.cdr.detectChanges();
      this.cdr.markForCheck();
      try { this.outputUpdated.emit(this.data); } catch (e) { /* swallow */ }
    }, 0);
  }

  sortTableData() {
    if (this.sortingInProgress) {
      return;
    }

    if (!this.sortColumn || !this.data || !this.data.Column) {
      return;
    }
    this.sortingInProgress = true;
    // Disable sorting if nesting is present in rows or columns
    const hasRowNesting = this.data['Row'] && this.data['Row'][0] && this.data['Row'][0].Data && this.data['Row'][0].Data[0] && this.data['Row'][0].Data[0].NestedArray && this.data['Row'][0].Data[0].NestedArray.length > 0;
    const hasColNesting = this.data['Column'] && this.data['Column'][0] && this.data['Column'][0].Data && this.data['Column'][0].Data[0] && this.data['Column'][0].Data[0].NestedArray && this.data['Column'][0].Data[0].NestedArray.length > 0;
    if (hasRowNesting || hasColNesting) {
      this.sortingInProgress = false;
      return;
    }
    // Find the column and its index
    let colInfo: any = null;
    let colIndex = -1;
    this.data.Column.forEach((col: any, idx: number) => {
      col.Data.forEach((colItem: any) => {
        if (colItem.Child === this.sortColumn) {
          colInfo = colItem;
          colIndex = idx;
        }
      });
    });
    if (!colInfo || colIndex === -1) {
      this.sortingInProgress = false;
      return;
    }
    const allRowItems: any[] = [];
    this.data.Row.forEach((row: any) => {
      row.Data.forEach((item: any) => {
        const sortValue = this.getCellValue(item, colInfo);
        allRowItems.push({
          rowItem: item,
          sortValue: sortValue,
          rowParent: row
        });
      });
    });

    // Capture original row order before the first sort so it can be restored
    if (!this.originalRowData) {
      this.originalRowData = _.cloneDeep(allRowItems.map((item: any) => item.rowItem));
    }

    const brandGroups: { [key: string]: number } = {};
    allRowItems.forEach((item: any) => {
      const childName = item.rowItem.Child || '';
      let parts = childName.split('|');
      if (parts.length < 3) {
        parts = childName.split(':');
      }

      if (parts.length >= 3) {
        const brandKey = parts[1].trim();
        brandGroups[brandKey] = (brandGroups[brandKey] || 0) + 1;
      }
    });

    const needsBrandGrouping = Object.keys(brandGroups).some(key => brandGroups[key] > 1);
    let sortedRowItems: any[] = [];
    if (needsBrandGrouping) {
      const groupedByBrand: { [key: string]: any[] } = {};
      const brandMaxValues: { [key: string]: number } = {};
      allRowItems.forEach((item: any) => {
        const childName = item.rowItem.Child || '';
        // Try both pipe and colon delimiters
        let parts = childName.split('|');
        if (parts.length < 3) {
          parts = childName.split(':');
        }
        const brandKey = parts.length >= 3 ? parts[1].trim() : childName;

        if (!groupedByBrand[brandKey]) {
          groupedByBrand[brandKey] = [];
          brandMaxValues[brandKey] = -Infinity;
        }
        groupedByBrand[brandKey].push(item);
        brandMaxValues[brandKey] = Math.max(brandMaxValues[brandKey], item.sortValue);
      });

      const brandOrder = Object.keys(groupedByBrand).sort((a, b) => {
        const aMax = brandMaxValues[a];
        const bMax = brandMaxValues[b];
        if (aMax === bMax) return 0;
        if (this.sortDirection === 'asc') {
          return aMax > bMax ? 1 : -1;
        } else {
          return aMax > bMax ? -1 : 1;
        }
      });

      brandOrder.forEach((brandKey: string) => {
        const brandGroup = groupedByBrand[brandKey];
        brandGroup.sort((a: any, b: any) => {
          if (a.sortValue === b.sortValue) return 0;
          if (this.sortDirection === 'asc') {
            return a.sortValue > b.sortValue ? 1 : -1;
          } else {
            return a.sortValue > b.sortValue ? -1 : 1;
          }
        });
        sortedRowItems.push(...brandGroup);
      });
    } else {
      sortedRowItems = [...allRowItems];
      sortedRowItems.sort((a: any, b: any) => {
        if (a.sortValue === b.sortValue) return 0;
        if (this.sortDirection === 'asc') {
          return a.sortValue > b.sortValue ? 1 : -1;
        } else {
          return a.sortValue > b.sortValue ? -1 : 1;
        }
      });
    }

    const newRowData = sortedRowItems.map((item: any) => item.rowItem);
    const newRow = { ...this.data.Row[0], Data: newRowData };
    const newRowArray = [newRow];

    this.data = {
      ...this.data,
      Row: newRowArray
    };

    setTimeout(() => {
      this.cdr.detectChanges();
      this.cdr.markForCheck();
      this.sortingInProgress = false;
      try {
        // Emit the updated data object so parent components can persist the sorted order
        this.outputUpdated.emit(this.data);
      } catch (e) {
        // swallow any emitter errors
      }
    }, 0);
  }
  getCellValue(rowItem: any, colItem: any): number {
    // Always try to get percentage or numeric cell value
    let val: any = this.getPercentage(rowItem, colItem);

    if (val === null || val === undefined) return 0;

    // Convert HTML/percentage/strings → number
    if (typeof val === 'string') {
      // Removes %, commas, spaces, HTML, etc.
      val = val.split("|")[0].trim();
      val = val.replace(/<[^>]*>/g, '').replace(/[^\d.-]/g, '');
      const num = parseFloat(val);
      return isNaN(num) ? 0 : num;
    }

    // If already a number
    if (typeof val === 'number') return val;

    return 0;
  }


  // getCellValue(rowItem: any, colItem: any): any {
  //   // Use getPercentage or similar logic to get the value for sorting
  //   const val = this.getPercentage(rowItem, colItem);
  //   // Remove any formatting (like % or HTML)
  //   if (typeof val === 'string') {
  //     const num = parseFloat(val.replace(/[^\d.-]/g, ''));
  //     return isNaN(num) ? 0 : num;
  //   }
  //   return val;
  // }

  getOutputData() {
    this.dataSharingService.crossTabOuputDataSubject$.subscribe(({ data }) => {
      
      if (this.isFromAskMe && this.data && this.data.flatData) {
        return;
      }
      this.crossTabReceivedData = data;
      this.originalRowData = null; // Reset stored original order when fresh data arrives

      if (typeof window !== 'undefined') {
        (window as any)['CrossTabData'] = _.cloneDeep(data);
      }

      // Re-apply sorting if there's an active sort when new data is received (significance updates, etc.)
      if (this.sortColumn && this.sortDirection !== 'ascd') {
        // Small delay to ensure data is processed before sorting
        setTimeout(() => {
          this.sortTableData();
          // Notify charts about the maintained sorting to keep them in sync
          this.dataSharingService.setTableSorting(this.sortColumn, this.sortDirection);
          this.cdr.markForCheck();
        }, 10);
      }
    });

  }

  onUserAction() {
    this.getOutputData();
    // Maintain current sort state after user action with longer delay for significance changes
    setTimeout(() => {
      this.maintainSortState();
    }, 150);
  }

  extractMarkdownTable(): string {
    const tableDiv = this.elementRef.nativeElement.querySelector('.table-preview');
    if (!tableDiv) return '';

    let markdown = '';

    // Extract column headers
    const colHeadersElements = tableDiv.querySelectorAll('.preview-columns-header .row-header-title, .preview-columns-header .col-nest-header-txt');
    const headers: string[] = ['Row Name'];
    colHeadersElements.forEach((el: any) => {
      const text = el.textContent?.trim();
      // Skip empty or purely whitespace headers
      if (text) headers.push(text);
    });

    if (headers.length === 1) return ''; // No valid columns found

    markdown += `| ${headers.join(' | ')} |\n`;
    markdown += `| ${headers.map(() => '---').join(' | ')} |\n`;

    // Extract row headers and respective cells
    const rowHeadersElements = tableDiv.querySelectorAll('.table-rows-header-block .row-col-header-title, .table-rows-header-block .row-nest-header-txt');
    const dataContainers = tableDiv.querySelectorAll('.preview-table-body .row-data-container');

    for (let i = 0; i < dataContainers.length; i++) {
      const rowHead = rowHeadersElements[i]?.textContent?.trim() || `Row ${i + 1}`;
      const cells = dataContainers[i].querySelectorAll('.cell-data');
      let rowArray = [rowHead];
      cells.forEach((cell: any) => {
        rowArray.push(cell.textContent?.trim() || '');
      });
      markdown += `| ${rowArray.join(' | ')} |\n`;
    }

    return markdown;
  }

//   getHeader(item: any) {
//   if (!item) {
//     return item;
//   }

//   let splitedText: string[];

//   if (item.indexOf('|') > -1) {
//     splitedText = item.split('|');
//   } else {
//     const colonMatch = item.match(/^(.*?)\s*:\s*(.*)$/);
//     if (colonMatch) {
//       splitedText = [colonMatch[1].trim(), colonMatch[2].trim()];
//     } else {
//       return item;
//     }
//   }

//   // Only rearrange if it's a Custom Percentage Variable or Custom Mean Variable
//   if (splitedText.length > 1 && (splitedText[0].includes('Custom Percentage Variable') || splitedText[0].includes('Custom Mean Variable'))) {
//     const lastPart = splitedText[splitedText.length - 1];
//     const firstParts = splitedText.slice(0, -1).join(' : ');
//     return `${lastPart} (${firstParts})`;
//   }
//   if (splitedText.length > 1 && splitedText[0].toUpperCase().includes('MMA')) {
//     return splitedText.join(' : ');
//   }
//   return splitedText[splitedText.length - 1];
// }


  // getHeader(item: any) {
  //   if (item) {
  //     let splitedText = item.split('|');
  //     // Only rearrange if it's a Custom Percentage Variable or Custom Mean Variable
  //     if (splitedText.length > 1 && (splitedText[0].includes('Custom Percentage Variable') || splitedText[0].includes('Custom Mean Variable'))) {
  //       const lastPart = splitedText[splitedText.length - 1];
  //       const firstParts = splitedText.slice(0, -1).join(' : ');
  //       return `${lastPart} (${firstParts})`;
  //     }
  //     return splitedText.join(' : ');
  //   }
  //   return item;
  // }
  getHeader(item: any) {
  if (!item) {
    return item;
  }

  let splitedText: string[];
  if (item.indexOf('|') > -1) {
    splitedText = item.split('|');
  } else {
    const colonMatch = item.match(/^(.*?)\s*:\s*(.*)$/);
    if (colonMatch) {
      splitedText = [colonMatch[1].trim(), colonMatch[2].trim()];
    } else {
      return item;
    }
  }

      if (splitedText.length > 1 && (splitedText[0].includes('Custom Percentage Variable') || splitedText[0].includes('Custom Mean Variable'))) {
        const lastPart = splitedText[splitedText.length - 1];
        const firstParts = splitedText.slice(0, -1).join(' : ');
        return `${lastPart} (${firstParts})`;
      }

  if (splitedText.length > 1) {
    const label = splitedText[0];

    if (label.toUpperCase().includes('MMA')) {
      return splitedText.join(' : ');
    }

    const showValueOnly = ['Markets', 'Categories', 'Sub Categories', 'Month', 'Quarter', 'Week', 'Year'];
    if (showValueOnly.includes(label) || (this.isBrandAnalysis && label === 'Brands')) {
      return splitedText[splitedText.length - 1];
    }
  }
  return splitedText.join(' : ');
}

  getParentRowHeader(item: any): string {
    if (item == 'Total') {
      const has2ndLevelRowNesting = this.data?.Row?.[0]?.Data?.[0]?.NestedArray?.[0]?.NestedArray?.length > 0;
      if (has2ndLevelRowNesting) {
        return 'Parent Row Total';
      } else {
        return 'Row Total';
      }
    } else {
      return this.getHeader(item);
    }
  }

  getParentColumnHeader(item: any): string {
    if (item == 'Total') {
      const has2ndLevelColumnNesting = this.data?.Column?.[0]?.Data?.[0]?.NestedArray?.[0]?.NestedArray?.length > 0;
      if (has2ndLevelColumnNesting) {
        return 'Parent Column Total';
      } else {
        return 'Column Total';
      }
    } else {
      return this.getHeader(item);
    }
  }

  //To get the count of number of rows along with nesting
  getrowCount() {
    let rowCount = 0;
    if (
      this.data != null &&
      this.data != undefined &&
      this.data['Row'] != undefined
    ) {
      this.data.Row.forEach((x: any) => {
        rowCount = rowCount + x.Data.length;
      });
      if (this.data['RowNesting'] != undefined) {
        this.data.RowNesting.forEach((x: any) => {
          rowCount = rowCount * x.Data.length;
        });
      }
    }
    return rowCount;
  }

  getWidthofCellOnCount(panel: string, panelNested: string): number {
    let rowCellCount = 1;
    let count = 1;
    if (this.data && this.data[panel]) {
      const nestedData = this.data[panelNested];

      if (nestedData) {
        nestedData.forEach((nestedItem: any) => {
          rowCellCount *= nestedItem.Data.length;
          nestedItem.Data.forEach((nestedArrayData: any) => {
            if (nestedArrayData.NestedArray.length > 0) {
              rowCellCount *= nestedArrayData.NestedArray.length;
            }
          });
        });
      }
    }

    const heightWidth = panel === 'Row' ? this.cellHeight : this.cellWidth;
    return rowCellCount * heightWidth;
  }

  getWidthofCellOnCount_Row(
    panel: string,
    panelNested: string,
    rowData: any
  ): number {
    let rowCellCount = 1;
    if (rowData.NestedArray.length > 0) {
      rowData.NestedArray.forEach((nestedItem: any) => {
        if (nestedItem.NestedArray.length > 0) {
          rowCellCount *= nestedItem.NestedArray.length;
        } else {
          rowCellCount = rowData.NestedArray.length;
        }
      });
    }
    const heightWidth = panel === 'Row' ? this.cellHeight : this.cellWidth;
    return rowCellCount * heightWidth;
  }

  getHeightOfCell(nesting: any): number {
    let height = 45;
  if(nesting.Child == "Total"){
      if (nesting.NestedArray.length > 0) {
        if(nesting.NestedArray[0].Child == "Total"){
          height = height * 2;
        }
      }
    }

    return height;
  }
  getWidthofCellOnCount_Column(
    panel: string,
    panelNested: string,
    rowData: any
  ): number {
    let rowCellCount = 0;

    if (rowData.NestedArray.length > 0) {
      rowData.NestedArray.forEach((nestedItem: any) => {
        if (nestedItem.NestedArray.length > 0) {
          rowCellCount += nestedItem.NestedArray.length;
        } else {
          rowCellCount = rowData.NestedArray.length;
        }
      });
    } else {
      rowCellCount = 1;
    }
    const heightWidth = panel === 'Row' ? this.cellHeight : this.cellWidth;
    return rowCellCount * heightWidth;
  }

  getNestingCount(index: number, panel: string, panelNested: string) {
    let rowCellCount = 1;

    if (
      this.data != null &&
      this.data != undefined &&
      this.data[panel] != undefined
    ) {
      if (this.data[panelNested] != undefined) {
        for (let i = 0; i <= index; i++) {
          rowCellCount = rowCellCount * this.data[panelNested][i].Data.length;
        }
      }
    }
    return this.getWidthofCellOnCount(panel, panelNested) / rowCellCount;
  }

  getRowNestingCount(index: number, panel: string, panelNested: string) {
    let rowCellCount = 1;

    if (
      this.data != null &&
      this.data != undefined &&
      this.data[panel] != undefined
    ) {
      if (this.data[panelNested] != undefined) {
        for (let i = 0; i <= index; i++) {
          rowCellCount = rowCellCount * this.data[panelNested][i].Data.length;
        }
      }
    }
    return this.getWidthofCellOnCount(panel, panelNested) / rowCellCount;
  }
  //get the column and column nested count
  getrowCellCount() {
    let rowCellCount = 0;
    if (
      this.data != null &&
      this.data != undefined &&
      this.data['Column'] != undefined
    ) {
      this.data.Column.forEach((x: any) => {
        rowCellCount = rowCellCount + x.Data.length;
      });
      if (this.data['ColNesting'] != undefined) {
        this.data.ColNesting.forEach((x: any) => {
          rowCellCount = rowCellCount * x.Data.length;
        });
      }
    }
    return rowCellCount;
  }

  //get the count for row or column nesting count
  getNestedLevelCount(index: number, nesting: any) {
    let count = 1;
    for (let i = 0; i < index; i++) {
      count = count * nesting[i].Data.length;
    }
    return count;
  }

  // Margin for the scroll based on the count of nested rows and columns
  getMarginForScroll() {
    let colRowCount = 0;
    let RowColCount = 0;

    if (this.data != null && this.data != undefined) {
      if (this.data['Column'] != undefined && this.data['Column'].length > 0)
        colRowCount = 1;
      if (
        this.data['Column'] != undefined &&
        this.data['Column'].length > 0 &&
        this.data['Column'][0]['Data'][0]['NestedArray'].length > 0
      )
        colRowCount = 2;
      if (
        this.data['Column'] != undefined &&
        this.data['Column'].length > 0 &&
        this.data['Column'][0]['Data'][0]['NestedArray'].length > 0 &&
        this.data['Column'][0]['Data'][0]['NestedArray'][0]['NestedArray']
          .length > 0
      )
        colRowCount = 3;
      if (this.data['Row'] != undefined && this.data['Row'].length > 0)
        RowColCount = 1;
      if (
        this.data['Row'] != undefined &&
        this.data['Row'].length > 0 &&
        this.data['Row'][0]['Data'][0]['NestedArray'].length > 0
      )
        RowColCount = 2;
      if (
        this.data['Row'] != undefined &&
        this.data['Row'].length > 0 &&
        this.data['Row'][0]['Data'][0]['NestedArray'].length > 0 &&
        this.data['Row'][0]['Data'][0]['NestedArray'][0]['NestedArray'].length >
        0
      )
        RowColCount = 3;
    }
    return `${45 * colRowCount}px 0 0 ${200 * RowColCount}px`;
  }

  getPercentage(rowItemData: any, colItemData: any) {
    let rowItem = rowItemData;
    let colItem = colItemData;
    (window as any)['CrossTabData_Index'] = [];
    if (this.isTranspose) {
      rowItem = { ...colItemData };
      colItem = { ...rowItemData };
    }

    if (this.isFromAskMe && !this.crossTabReceivedData?.length) {
    return 'NA';
    }

    // Find the row that matches the response codes
    const matchingRow = this.crossTabReceivedData.find(
      (row: CrossTabOutputItem) => {
        if (rowItem.isDerived && rowItem.CalculationType.toLowerCase() == 'mean' && this.selectedDataFormat == 'Respondent Level') {
          return (
            row.colResponseCode == colItem.responsecode &&
            row.rowVariableCode == rowItem.variablecode &&
            row.colVariableCode == colItem.variablecode &&
            row.colChild == colItem.Child &&
            row.CalculationType.toLowerCase() == 'mean'
          )
        }
        else if (rowItem.isDerived && rowItem.CalculationType.toLowerCase() == 'mean' && this.selectedDataFormat != 'Respondent Level') {
          return (
            row.colResponseCode == colItem.responsecode &&
            row.rowVariableCode == rowItem.variablecode &&
            row.colVariableCode == colItem.variablecode &&
            row.colChild == colItem.Child &&
            row.CalculationType.toLowerCase() == 'mean'
            && row.rowResponseCode === rowItem.responsecode
          )
        }
        else if (rowItem.isDerived && rowItem.CalculationType.toLowerCase() == 'meanpercentage' && this.selectedDataFormat == 'Respondent Level') {
          return (
            row.colResponseCode == colItem.responsecode &&
            row.rowVariableCode == rowItem.variablecode &&
            row.colVariableCode == colItem.variablecode &&
            row.colChild == colItem.Child &&
            row.CalculationType.toLowerCase() == 'meanpercentage'
          )
        }
        else if (rowItem.isDerived && rowItem.CalculationType.toLowerCase() == 'meanpercentage' && this.selectedDataFormat != 'Respondent Level') {
          return (
            row.colResponseCode == colItem.responsecode &&
            row.rowVariableCode == rowItem.variablecode &&
            row.colVariableCode == colItem.variablecode &&
            row.colChild == colItem.Child &&
            row.CalculationType.toLowerCase() == 'meanpercentage'
            && row.rowResponseCode === rowItem.responsecode
          )
        }
        else if (row.isCustomGroup) {
          return (
            row.rowResponseCode == rowItem.responsecode &&
            row.colResponseCode == colItem.responsecode &&
            row.rowVariableCode == rowItem.variablecode &&
            row.colVariableCode == colItem.variablecode &&
            row.colChild == colItem.Child &&
            (row.customGroupId == colItem.customGroupId ||
              row.customGroupId == colItem.customGroupId)
          );
        } else if (row.isCustomCalculation) {
          return (
            row.rowResponseCode == rowItem.responsecode &&
            row.colResponseCode == colItem.responsecode &&
            row.rowVariableCode == rowItem.variablecode &&
            row.colVariableCode == colItem.variablecode &&
            row.colChild == colItem.Child &&
            (row.customCalculationId == rowItem.customCalculationId ||
              row.customCalculationId == colItem.customCalculationId)
          );
        } else {
          return (
            row.rowResponseCode == rowItem.responsecode &&
            row.colResponseCode == colItem.responsecode &&
            row.rowVariableCode == rowItem.variablecode &&
            row.colVariableCode == colItem.variablecode &&
            row.colChild == colItem.Child
          );
        }
      });

    if (this.isIndex && this.selectedIndex != "") {
      const matchingReferenceRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) => {
          if (row.isDerived && row.CalculationType.toLowerCase() == 'mean') {
            return (
              (row.CalculationType.toLowerCase() == 'mean' &&
                row.rowVariableCode == rowItemData.variablecode &&
                row.colChild == this.selectedIndex)
            );
          } else {
            return (
              (
                row.rowResponseCode == rowItemData.responsecode &&
                row.rowVariableCode == rowItemData.variablecode &&
                row.colChild == this.selectedIndex)

            );
          }
        }
      );
      if (matchingReferenceRow != undefined) {
        const matchingRowCopy = { ...matchingRow };
        const matchingReferenceRowCopy = { ...matchingReferenceRow };

        if (this.selectedWeight == "Unweighted") {
          matchingRowCopy.totalUnweightedShare = isNaN(matchingRowCopy.totalUnweightedShare / matchingReferenceRowCopy.totalUnweightedShare) ? null : isFinite(matchingRowCopy.totalUnweightedShare / matchingReferenceRowCopy.totalUnweightedShare) ? (matchingRowCopy.totalUnweightedShare / matchingReferenceRowCopy.totalUnweightedShare) : null;
          matchingRowCopy.answerUnweightedShare = isNaN(matchingRowCopy.answerUnweightedShare / matchingReferenceRowCopy.answerUnweightedShare) ? null : isFinite(matchingRowCopy.answerUnweightedShare / matchingReferenceRowCopy.answerUnweightedShare) ? (matchingRowCopy.answerUnweightedShare / matchingReferenceRowCopy.answerUnweightedShare) : null;

          matchingRowCopy.totalUnweightedShare_Index = matchingRowCopy.totalUnweightedShare;
          matchingRowCopy.answerUnweightedShare_Index = matchingRowCopy.answerUnweightedShare;

        }
        else {
          matchingRowCopy.totalWeightedShare = isNaN(matchingRowCopy.totalWeightedShare / matchingReferenceRowCopy.totalWeightedShare) ? null : isFinite(matchingRowCopy.totalWeightedShare / matchingReferenceRowCopy.totalWeightedShare) ? (matchingRowCopy.totalWeightedShare / matchingReferenceRowCopy.totalWeightedShare) : null;
          matchingRowCopy.answerWeightedShare = isNaN(matchingRowCopy.answerWeightedShare / matchingReferenceRowCopy.answerWeightedShare) ? null : isFinite(matchingRowCopy.answerWeightedShare / matchingReferenceRowCopy.answerWeightedShare) ? (matchingRowCopy.answerWeightedShare / matchingReferenceRowCopy.answerWeightedShare) : null;

          matchingRowCopy.totalWeightedShare_Index = matchingRowCopy.totalWeightedShare;
          matchingRowCopy.answerWeightedShare_Index = matchingRowCopy.answerWeightedShare;
        }
        let calculatedPercentage: number;
        let ShareNumber = this.isTotalBase
          ? (this.selectedWeight == "Unweighted" ? matchingRowCopy.totalUnweightedShare_Index : matchingRowCopy.totalWeightedShare_Index)
          : (this.selectedWeight == "Unweighted" ? matchingRowCopy.answerUnweightedShare_Index : matchingRowCopy.answerWeightedShare_Index);

        if (ShareNumber == null) {
          matchingRowCopy.ColorCode = "black";
          return 'NA';
        }
        else {
          calculatedPercentage = ((matchingRowCopy.isDerived && this.isIndex && this.selectedIndex != ""))
            ? Number((ShareNumber * 100).toFixed(this.decimalPlaces)) : ((matchingRowCopy.isDerived && matchingRowCopy.CalculationType == 'mean') || matchingRowCopy.isCustomCalculation) ? Number(ShareNumber.toFixed(1))
              : Number((ShareNumber * 100).toFixed(this.decimalPlaces));

          if (calculatedPercentage < (100 - this.ThresholdValue)) {
            matchingRowCopy.ColorCode = "red";
          }
          else if (calculatedPercentage > (100 + this.ThresholdValue)) {
            matchingRowCopy.ColorCode = "green";
          }
          else {
            matchingRowCopy.ColorCode = "black";
          }
        }
        if (matchingRow != null) {
          matchingRow.totalWeightedShare_Index = matchingRowCopy.totalWeightedShare_Index;
          matchingRow.answerWeightedShare_Index = matchingRowCopy.answerWeightedShare_Index;
          matchingRow.ColorCode = matchingRowCopy.ColorCode;
        }
        // if (typeof window !== 'undefined') {
        //   (window as any)['CrossTabData_Index'] = _.cloneDeep(this.crossTabReceivedData);
        // }

        return this.calculatePercentage(matchingRowCopy);
      }
    }


    return this.calculatePercentage(matchingRow);
  }

  private calculatePercentage(
    matchingRow: CrossTabOutputItem
  ): number | string {

    if (this.selectedDataFormat != 'Respondent Level') {
      //let result: number | string = 'NA';
      //result = `${matchingRow.value}`;
      //return result;
      let calculatedPercentage: number;
      let calcBase: number;
      let calcWeightedBase: number;
      if (matchingRow == undefined) {
        return 'NA';
      }
      let ShareNumber = matchingRow.value;
      let BaseValue = matchingRow.value;
      let WeightedBaseValue = matchingRow.value;

      if (matchingRow) {
        calcBase = BaseValue == null ? 0 : Math.round(BaseValue);
        calcWeightedBase =
          WeightedBaseValue == null ? 0 : Math.round(WeightedBaseValue);
        if (this.isSampleSize) {
          return calcBase !== 0 ? calcBase : 'NA';
        } else if (this.isWeightedLabel) {
          return calcWeightedBase !== 0 ? calcWeightedBase : 'NA';
        }
        else {
          let result: number | string = 'NA';

          if (ShareNumber == null) {
            return 'NA';
          }
          calculatedPercentage = ((matchingRow.isDerived && matchingRow.CalculationType.toLowerCase() == 'mean') || (matchingRow.isCustomCalculation)) ? Number(ShareNumber.toFixed(this.decimalPlaces))
            : Number((ShareNumber * 100).toFixed(this.decimalPlaces));

          if (!this.isSampleSizeBlock) {
            if ((matchingRow.isDerived && matchingRow.CalculationType.toLowerCase() == "mean") || (matchingRow.isCustomCalculation)) {
              result = `${calculatedPercentage}`;
            }
            else {
              result = `${calculatedPercentage}%`;
            }
            return result;
          }
          else {
            if (calcBase !== 0) {
              if ((matchingRow.isDerived && matchingRow.CalculationType.toLowerCase() == "mean") || (matchingRow.isCustomCalculation)) {
                if (calcBase < this.InsufficientBaseSample) {
                  result = 'I/S';
                }
                else if (calcBase < this.LowBaseSample) {
                  result = `${calculatedPercentage} *`;
                }
                else {
                  result = `${calculatedPercentage}`;
                }
              }
              else {
                if (calcBase < this.InsufficientBaseSample) {
                  result = 'I/S';
                }
                else if (calcBase < this.LowBaseSample) {
                  result = `${calculatedPercentage}% *`;
                }
                else {
                  result = `${calculatedPercentage}% `;
                }
              }
              return result;
            }
            else {
              return 'NA';
            }

          }
        }
      }
    }
    else {

      let calculatedPercentage: number;
      let calcBase: number;
      let SigNumber: number | string;
      let changePy: number | string;
      let calcWeightedBase: number;
      let calcWeightedNumerator: number;
      let calcUnWeightedNumerator: number;
      if (matchingRow == undefined) {
        return 'NA';
      }

      const isCustomMeanVariable = matchingRow.rowChild && matchingRow.rowChild.includes('Custom Mean Variable');

      let ShareNumber = (this.selectedDataFormat != 'Respondent Level' ? matchingRow.value : (
        isCustomMeanVariable
          ? (this.selectedWeight == "Unweighted" ? matchingRow.answerUnweightedShare : matchingRow.answerWeightedShare)
          : (this.isTotalBase
            ? (this.selectedWeight == "Unweighted" ? matchingRow.totalUnweightedShare : matchingRow.totalWeightedShare)
            : (this.selectedWeight == "Unweighted" ? matchingRow.answerUnweightedShare : matchingRow.answerWeightedShare))));
      const isMeanRow = isCustomMeanVariable || (matchingRow.isDerived && matchingRow.CalculationType.toLowerCase() == 'mean');

      let BaseValue = isMeanRow
        ? matchingRow.answerUnweightedBase
        : (this.isTotalBase ? matchingRow.totalUnweightedBase : matchingRow.answerUnweightedBase);
      let WeightedBaseValue = isMeanRow
        ? (this.selectedWeight == "Unweighted" ? matchingRow.answerUnweightedBase : matchingRow.answerWeightedBase)
        : (this.isTotalBase
          ? (this.selectedWeight == "Unweighted" ? matchingRow.totalUnweightedBase : matchingRow.totalWeightedBase)
          : (this.selectedWeight == "Unweighted" ? matchingRow.answerUnweightedBase : matchingRow.answerWeightedBase));

      let numeratorValue = isMeanRow
        ? matchingRow.answerWeightedNumerator
        : (this.isTotalBase ? matchingRow.totalWeightedNumerator : matchingRow.answerWeightedNumerator);

      let UnWeightednumeratorValue = isMeanRow
        ? matchingRow.CurrentNumeratorUnWeighted_Answer
        : (this.isTotalBase ? matchingRow.CurrentNumeratorUnWeighted_Total : matchingRow.CurrentNumeratorUnWeighted_Answer);

      this.Sig_LowerLimt = this.significanceService.getSigLowerLimit(this.DefaultConfidenceLevel || '95%');
      this.Sig_UpperLimt = this.significanceService.getSigUpperLimit(this.DefaultConfidenceLevel || '95%');
      
      let SignificanceValue = isMeanRow
        ? matchingRow.Significance_Answer
        : (this.isTotalBase ? matchingRow.Significance_Total : matchingRow.Significance_Answer);
      let changevsPY = isMeanRow
        ? matchingRow.changeVsPY_Answer
        : (this.isTotalBase ? matchingRow.changeVsPY_Total : matchingRow.changeVsPY_Answer);
      if (matchingRow) {
        calcBase = BaseValue == null ? 0 : Math.round(BaseValue);
        calcWeightedBase =
          WeightedBaseValue == null ? 0 : Number(WeightedBaseValue.toFixed(this.decimalPlaces));
        calcWeightedNumerator =
          numeratorValue == null ? 0 : Number(numeratorValue.toFixed(this.decimalPlaces));
        calcUnWeightedNumerator =
          UnWeightednumeratorValue == null ? 0 : Math.round(UnWeightednumeratorValue);
        if (this.isSampleSize) {
          return calcBase !== 0 ? calcBase : 'NA';
        } else if (this.isWeightedLabel) {
          return calcWeightedBase !== 0 ? calcWeightedBase : 'NA';
        }
        else if (this.isWeightedNumerator) {
          return calcWeightedNumerator !== 0 ? calcWeightedNumerator : 'NA';
        }
        else if (this.isUnWeightedNumerator) {
          return calcUnWeightedNumerator !== 0 ? calcUnWeightedNumerator : 'NA';
        }
        else {
          let result: number | string = 'NA';

          if (ShareNumber == null) {
            return 'NA';
          }
          calculatedPercentage = (matchingRow.isDerived && this.isIndex && this.selectedIndex != "")
            ? Number((ShareNumber * 100).toFixed(this.decimalPlaces)) : ((matchingRow.isDerived && matchingRow.CalculationType.toLowerCase() == 'mean') || ((matchingRow.isCustomCalculation)) || (matchingRow.rowChild && matchingRow.rowChild.includes('Custom Mean Variable'))) ? Number(ShareNumber.toFixed(1))
              : Number((ShareNumber * 100).toFixed(this.decimalPlaces));


          if (calcBase !== 0) {
            if (calcBase < this.InsufficientBaseSample) {
              result = 'I/S';
            }
            else {
              SigNumber =
                SignificanceValue == null || isNaN(SignificanceValue)
                  ? 0
                  : SignificanceValue;
              //changePy =
              //  changevsPY == null
              //    ? 'NA'
              //    : matchingRow.isDerived
              //      ? Number(changevsPY.toFixed(1))
              //      : Number((changevsPY * 100).toFixed(1));

              changePy =
                changevsPY == null
                  ? 'NA'
                  : ((matchingRow.isDerived &&
                    (matchingRow.rowChild.includes("Mean") || matchingRow.CalculationType.toLowerCase() == 'mean') &&
                    matchingRow.CalculationType.toLowerCase() !== "meanpercentage") ||
                    matchingRow.isCustomCalculation)
                    ? Number(changevsPY.toFixed(this.decimalPlaces))
                    : this.customRound(changevsPY * 100);

                    console.log({
  row: matchingRow.rowChild,
  changePy,
  SigNumber
});
// Bug Fix : Do not show significance color when mean significance value is 0
if (
  ((matchingRow.isDerived &&
    (matchingRow.rowChild?.includes('Mean') ||
      matchingRow.CalculationType?.toLowerCase() === 'mean')) ||
    matchingRow.isCustomCalculation) &&
  Number(changePy) === 0
) {
  SigNumber = 0;
}
              if ((matchingRow.isDerived
                && (matchingRow.rowChild.includes("Mean") || matchingRow.CalculationType.toLowerCase() == "mean")
                && matchingRow.CalculationType.toLowerCase() !== "meanpercentage"
              ) || (matchingRow.isCustomCalculation)) {
                if (this.DefaultSampleSize == 'None') {
                  if (calcBase < this.LowBaseSample) {

                    if (this.isIndex && this.selectedIndex != "") {
                      if (calculatedPercentage < (100 - this.ThresholdValue)) {
                        result = `<span class="sigLower"> ${calculatedPercentage} *</span>`;
                      }
                      else if (calculatedPercentage > (100 + this.ThresholdValue)) {
                        result = `<span class="sigHigher"> ${calculatedPercentage} *</span>`;
                      }
                      else {
                        result = `${calculatedPercentage}`;
                      }
                    }
                    else {
                      result = `${calculatedPercentage} *`;
                    }
                  }
                  else {
                    if (this.isIndex && this.selectedIndex != "") {
                      if (calculatedPercentage < (100 - this.ThresholdValue)) {
                        result = `<span class="sigLower"> ${calculatedPercentage} </span>`;
                      }
                      else if (calculatedPercentage > (100 + this.ThresholdValue)) {
                        result = `<span class="sigHigher"> ${calculatedPercentage} </span>`;
                      }
                      else {
                        result = `${calculatedPercentage}`;
                      }
                    }
                    else {
                      result = `${calculatedPercentage}`;
                    }
                  }
                }
                else if (this.DefaultSampleSize == 'Pairwise') {
                  if (calcBase < this.LowBaseSample) {
                    result = `${calculatedPercentage} *`;
                  }
                  else {
                    result = `${calculatedPercentage}`;
                  }
                  let pairwiseText = this.pairWiseText(matchingRow.pairWiseValues);
                  if (pairwiseText != "") {
                    result = `${result} ${pairwiseText}`
                  }
                }
                else {
                  const shouldHideNAorZero = this.DefaultSampleSize === 'Column Total' &&
                    matchingRow.isTotal &&
                    (this.SignificanceVsReference === 'Parent Column Total' ||
                      this.SignificanceVsReference === 'Nesting Level Column Total' ||
                      changePy === 0 || changePy === '0');

                  if (calcBase < this.LowBaseSample) {
                    if (changevsPY == null || changePy == "NA") {
                      result = shouldHideNAorZero ? `${calculatedPercentage} *` : `${calculatedPercentage} * | NA`;
                    }
                    else if (shouldHideNAorZero && (changePy === 0 || changePy === '0')) {
                      result = `${calculatedPercentage} *`;
                    }
                    else {
                      if (SigNumber < this.Sig_LowerLimt) {
                        result = `${calculatedPercentage} * |&nbsp; <span class="sigLower"> ${changePy} </span>`;
                      } else if (SigNumber > this.Sig_UpperLimt) {
                        result = `${calculatedPercentage} * |&nbsp; <span class="sigHigher"> ${changePy} </span>`;
                      } else {
                        result = `${calculatedPercentage} * |&nbsp; ${changePy}`;
                      }
                    }
                  }
                  else {
                    if (changevsPY == null || changePy == "NA") {
                      result = shouldHideNAorZero ? `${calculatedPercentage}` : `${calculatedPercentage} | NA`;
                    }
                    else if (shouldHideNAorZero && (changePy === 0 || changePy === '0')) {
                      result = `${calculatedPercentage}`;
                    }
                    else {
                      if (SigNumber < this.Sig_LowerLimt) {
                        result = `${calculatedPercentage} |&nbsp; <span class="sigLower"> ${changePy} </span>`;
                      } else if (SigNumber > this.Sig_UpperLimt) {
                        result = `${calculatedPercentage} |&nbsp; <span class="sigHigher"> ${changePy} </span>`;
                      } else {
                        result = `${calculatedPercentage} |&nbsp; ${changePy}`;
                      }
                    }
                  }
                }
              }
              else {
                if (this.DefaultSampleSize == 'None') {
                  if (calcBase < this.LowBaseSample) {
                    if (this.isIndex && this.selectedIndex != "") {
                      if (calculatedPercentage < (100 - this.ThresholdValue)) {
                        result = `<span class="sigLower"> ${calculatedPercentage} *</span>`;
                      }
                      else if (calculatedPercentage > (100 + this.ThresholdValue)) {
                        result = `<span class="sigHigher"> ${calculatedPercentage} *</span>`;
                      }
                      else {
                        result = `${calculatedPercentage} *`;
                      }
                    }
                    else {
                      result = `${calculatedPercentage}% *`;
                    }
                  }
                  else {
                    if (this.isIndex && this.selectedIndex != "") {
                      if (calculatedPercentage < (100 - this.ThresholdValue)) {
                        result = `<span class="sigLower"> ${calculatedPercentage} </span>`;
                      }
                      else if (calculatedPercentage > (100 + this.ThresholdValue)) {
                        result = `<span class="sigHigher"> ${calculatedPercentage} </span>`;
                      }
                      else {
                        result = `${calculatedPercentage}`;
                      }
                    }
                    else {
                      result = `${calculatedPercentage}%`;
                    }
                  }
                }
                else if (this.DefaultSampleSize == 'Pairwise') {
                  if (calcBase < this.LowBaseSample) {
                    result = `${calculatedPercentage}% *`;
                  }
                  else {
                    result = `${calculatedPercentage}%`;
                  }
                  let pairwiseText = this.pairWiseText(matchingRow.pairWiseValues);
                  if (pairwiseText != "") {
                    result = `${result} ${pairwiseText}`
                  }
                }
                else {
                  const shouldHideNAorZero = this.DefaultSampleSize === 'Column Total' &&
                    matchingRow.isTotal &&
                    (this.SignificanceVsReference === 'Parent Column Total' ||
                      this.SignificanceVsReference === 'Nesting Level Column Total' ||
                      changePy === 0 || changePy === '0');

                  if (calcBase < this.LowBaseSample) {
                    if (changevsPY == null || changePy == "NA") {
                      result = shouldHideNAorZero ? `${calculatedPercentage}% *` : `${calculatedPercentage}% * | NA`;
                    }
                    else if (shouldHideNAorZero && (changePy === 0 || changePy === '0')) {
                      result = `${calculatedPercentage}% *`;
                    }
                    else {
                      if (changevsPY == null || changePy == "NA") {
                        result = shouldHideNAorZero ? `${calculatedPercentage}% *` : `${calculatedPercentage}% * |&nbsp; NA`;
                      }
                      else {
                        if (SigNumber < this.Sig_LowerLimt) {
                          result = `${calculatedPercentage}% * |&nbsp; <span class="sigLower"> ${changePy}% </span>`;
                        } else if (SigNumber > this.Sig_UpperLimt) {
                          result = `${calculatedPercentage}% * |&nbsp; <span class="sigHigher"> ${changePy}% </span>`;
                        } else {
                          result = `${calculatedPercentage}% * |&nbsp; ${changePy}%`;
                        }
                      }
                    }
                  }
                  else {
                    if (changevsPY == null || changePy == "NA") {
                      result = shouldHideNAorZero ? `${calculatedPercentage}%` : `${calculatedPercentage}% |&nbsp; NA`;
                    }
                    else if (shouldHideNAorZero && (changePy === 0 || changePy === '0')) {
                      result = `${calculatedPercentage}%`;
                    }
                    else {
                      if (SigNumber < this.Sig_LowerLimt) {
                        result = `${calculatedPercentage}% |&nbsp; <span class="sigLower"> ${changePy}% </span>`;
                      } else if (SigNumber > this.Sig_UpperLimt) {
                        result = `${calculatedPercentage}% |&nbsp; <span class="sigHigher"> ${changePy}% </span>`;
                      } else {
                        result = `${calculatedPercentage}% |&nbsp; ${changePy}%`;
                      }
                    }
                  }
                }

              }
            }
          }

          return result;
        }
      }
      if (ShareNumber == null) {
        return 'NA';
      }
    }
    return 'NA';

  }

  private isColumnTotalNotCalculated(level1ColItem: any, level2ColItem?: any): boolean {
    const isExistingIndexException = this.isIndex
      && this.selectedIndex === 'Nesting Level Column Total'
      && level1ColItem?.Child !== 'Total'
      && level2ColItem?.Child === 'Total';

    if (isExistingIndexException) {
      return true;
    }

    const isSignificanceActive = !!this.DefaultSampleSize
      && this.DefaultSampleSize !== 'None';

    if (!isSignificanceActive) {
      return false;
    }

    const hasTimePeriodAtLevel = (level: number) =>
      this.columnNestingItems[level]?.Level0 === 'Time Period'
      || this.columnNestingSelections[level]?.Data?.some(
          (selection: any) => selection.isTimePeriod === true
        ) === true;

    if (level1ColItem?.Child === 'Total') {
      return hasTimePeriodAtLevel(0) || hasTimePeriodAtLevel(1);
    }

    return hasTimePeriodAtLevel(1) && level2ColItem?.Child === 'Total';
  }

  private formatColumnTotalSignificance(
    value: number | string,
    level1ColItem: any,
    level2ColItem?: any
  ): number | string {
    if (!this.isColumnTotalNotCalculated(level1ColItem, level2ColItem)) {
      return value;
    }

    const actualValue = value.toString().split('|')[0].trim();
    return `${actualValue} | Not Calculated`;
  }

  getPercentage_colNest(
    rowItem: any,
    colItem: any,
    colNestItem: any,
    colNestItemLevel2: any
  ): number | string {
    (window as any)['CrossTabData_Index'] = [];

    if (this.isTranspose) {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) => {
          if (row.isDerived) {
            return (
              row.CalculationType.toLowerCase() == 'mean' &&
              row.colResponseCode == rowItem.responsecode &&
              row.rowResponseCode == colItem.responsecode &&
              row.rowVariableCode == colItem.variablecode &&
              row.Level1RowResponseCode == colNestItem.responsecode &&
              row.Level1NestRowVariableCode == colNestItem.variablecode &&
              row.Level2RowResponseCode == colNestItemLevel2.responsecode &&
              row.Level2NestRowVariableCode == colNestItemLevel2.variablecode &&
              row.colChild == rowItem.Child
            );
          } else {
            return (
              row.colResponseCode == rowItem.responsecode &&
              row.rowResponseCode == colItem.responsecode &&
              row.rowVariableCode == colItem.variablecode &&
              row.Level1RowResponseCode == colNestItem.responsecode &&
              row.Level1NestRowVariableCode == colNestItem.variablecode &&
              row.Level2RowResponseCode == colNestItemLevel2.responsecode &&
              row.Level2NestRowVariableCode == colNestItemLevel2.variablecode &&
              row.colChild == rowItem.Child


            );
          }
        }
      );

      return this.formatColumnTotalSignificance(
        this.calculatePercentage(matchingRow),
        colNestItem,
        colNestItemLevel2
      );
    } else {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) => {
          if (row.isDerived && row.CalculationType.toLowerCase() == 'mean') {
            return (
              row.CalculationType.toLowerCase() == 'mean' &&
              // row.rowResponseCode == rowItem.responsecode &&    //COMMENTED TO HANDLE BUG 94163 - NA SCENARIO WHEN BRANDS PUNCHED AS RESPONSE
              row.rowVariableCode == rowItem.variablecode &&
              row.colResponseCode == colItem.responsecode &&
              row.colVariableCode == colItem.variablecode &&
              row.Level1ColResponseCode == colNestItem.responsecode &&
              row.Level1NestColVariableCode == colNestItem.variablecode &&
              row.Level2ColResponseCode == colNestItemLevel2.responsecode &&
              row.Level2NestColVariableCode == colNestItemLevel2.variablecode &&
              row.colChild == colItem.Child &&
              row.rowChild == rowItem.Child &&
              row.Level1ColChild == colNestItem.Child &&
              row.Level2ColChild == colNestItemLevel2.Child
            );
          } else if (row.isCustomGroup) {
            return (
              row.isCustomGroup == true &&
              // row.rowResponseCode == rowItem.responsecode &&
              row.rowVariableCode == rowItem.variablecode &&
              row.colResponseCode == colItem.responsecode &&
              row.colVariableCode == colItem.variablecode &&
              row.Level1ColResponseCode == colNestItem.responsecode &&
              row.Level1NestColVariableCode == colNestItem.variablecode &&
              row.Level2ColResponseCode == colNestItemLevel2.responsecode &&
              row.Level2NestColVariableCode == colNestItemLevel2.variablecode &&
              row.colChild == colItem.Child &&
              (row.customGroupId == rowItem.customGroupId ||
                row.customGroupId == colItem.customGroupId) &&
              row.rowChild == rowItem.Child &&
              row.Level1ColChild == colNestItem.Child &&
              row.Level2ColChild == colNestItemLevel2.Child
            );
          } else if (row.isCustomCalculation) {
            return (
              row.isCustomCalculation == true &&
              // row.rowResponseCode == rowItem.responsecode &&
              row.rowVariableCode == rowItem.variablecode &&
              row.colResponseCode == colItem.responsecode &&
              row.colVariableCode == colItem.variablecode &&
              row.Level1ColResponseCode == colNestItem.responsecode &&
              row.Level1NestColVariableCode == colNestItem.variablecode &&
              row.Level2ColResponseCode == colNestItemLevel2.responsecode &&
              row.Level2NestColVariableCode == colNestItemLevel2.variablecode &&
              row.colChild == colItem.Child &&
              (row.customCalculationId == rowItem.customCalculationId ||
                row.customCalculationId == colItem.customCalculationId) &&
              row.rowChild == rowItem.Child &&
              row.Level1ColChild == colNestItem.Child &&
              row.Level2ColChild == colNestItemLevel2.Child
            );
          } else {
            return (
              // row.rowResponseCode == rowItem.responsecode &&
              row.rowVariableCode == rowItem.variablecode &&
              row.colResponseCode == colItem.responsecode &&
              row.colVariableCode == colItem.variablecode &&
              (row.Level2ColResponseCode == colNestItemLevel2.responsecode || row.Level1ColResponseCode == colNestItem.responsecode) &&
              row.Level1NestColVariableCode == colNestItem.variablecode &&
              row.Level2ColResponseCode == colNestItemLevel2.responsecode &&
              row.Level2NestColVariableCode == colNestItemLevel2.variablecode &&
              row.colChild == colItem.Child &&
              row.rowChild == rowItem.Child &&
              row.Level1ColChild == colNestItem.Child &&
              row.Level2ColChild == colNestItemLevel2.Child
            );
          }
        }
      );
      return this.formatColumnTotalSignificance(
        this.calculatePercentage(matchingRow),
        colNestItem,
        colNestItemLevel2
      );
    }
  }

  getPercentage_RowNest(
    rowItem: any,
    RowlNestItem: any,
    RowNestItemLevel2: any,
    colItem: any
  ): number | string {
    (window as any)['CrossTabData_Index'] = [];
    if (this.isTranspose) {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) => {
          if (row.isDerived && row.CalculationType.toLowerCase() == 'mean') {
            return (
              row.CalculationType.toLowerCase() == 'mean' &&
              row.colResponseCode == rowItem.responsecode &&
              row.colVariableCode == rowItem.variablecode &&
              row.Level1ColResponseCode == RowlNestItem.responsecode &&
              row.Level1NestColVariableCode == RowlNestItem.variablecode &&
              row.Level2ColResponseCode == RowNestItemLevel2.responsecode &&
              row.Level2NestColVariableCode == RowNestItemLevel2.variablecode &&
              row.rowResponseCode == colItem.responsecode &&
              row.rowVariableCode == colItem.variablecode &&
              row.colChild == rowItem.Child &&
              row.rowChild == colItem.Child &&
              row.Level1ColChild == RowlNestItem.Child &&
              row.Level2ColChild == RowNestItemLevel2.Child
            );
          } else {
            return (
              row.colResponseCode == rowItem.responsecode &&
              // row.rowResponseCode == colItem.responsecode &&
              row.Level1ColResponseCode == RowlNestItem.responsecode &&
              row.colVariableCode == rowItem.variablecode &&
              row.rowVariableCode == colItem.variablecode &&
              row.Level1NestColVariableCode == RowlNestItem.variablecode &&
              row.Level2ColResponseCode == RowNestItemLevel2.responsecode &&
              row.Level2NestColVariableCode == RowNestItemLevel2.variablecode &&
              // row.rowResponseCode == colItem.responsecode &&
              row.rowVariableCode == colItem.variablecode &&
              row.colChild == rowItem.Child &&
              row.rowChild == colItem.Child &&
              row.Level1ColChild == RowlNestItem.Child &&
              row.Level2ColChild == RowNestItemLevel2.Child
            );
          }
        }
      );

      return this.calculatePercentage(matchingRow);
    } else {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) => {
          if (row.isDerived && row.CalculationType.toLowerCase() == 'mean') {
            return (
              row.CalculationType.toLowerCase() == 'mean' &&
              row.rowResponseCode == rowItem.responsecode &&
              row.rowVariableCode == rowItem.variablecode &&
              //row.Level1RowResponseCode == RowlNestItem.responsecode && //Unaidded awareness showing Toatal as NA
              (row.Level1NestRowVariableCode == RowlNestItem.variablecode || this.isBrandAnalysis) &&
              row.Level2RowResponseCode == RowNestItemLevel2.responsecode &&
              row.Level2NestRowVariableCode == RowNestItemLevel2.variablecode &&
              row.colResponseCode == colItem.responsecode &&
              row.rowChild == rowItem.Child &&
              row.colChild == colItem.Child &&
              row.Level1RowChild == RowlNestItem.Child &&
              row.Level2RowChild == RowNestItemLevel2.Child &&
              row.colVariableCode == colItem.variablecode
            );
          }
          else if (row.isCustomGroup) {
            return (
              row.isCustomGroup == true &&
              row.rowResponseCode == rowItem.responsecode &&
              row.rowVariableCode == rowItem.variablecode &&
              //row.Level1RowResponseCode == RowlNestItem.responsecode && //Unaidded awareness showing Toatal as NA
              (row.Level1NestRowVariableCode == RowlNestItem.variablecode || this.isBrandAnalysis) &&
              row.Level2RowResponseCode == RowNestItemLevel2.responsecode &&
              row.Level2NestRowVariableCode == RowNestItemLevel2.variablecode &&
              row.colResponseCode == colItem.responsecode &&
              row.colVariableCode == colItem.variablecode &&
              row.rowChild == rowItem.Child &&
              row.colChild == colItem.Child &&
              row.Level1RowChild == RowlNestItem.Child &&
              row.Level2RowChild == RowNestItemLevel2.Child &&
              (row.customGroupId == rowItem.customGroupId ||
                row.customGroupId == colItem.customGroupId)
            );
          } else if (row.isCustomCalculation) {
            return (
              row.isCustomCalculation == true &&
              row.rowResponseCode == rowItem.responsecode &&
              row.rowVariableCode == rowItem.variablecode &&
              //row.Level1RowResponseCode == RowlNestItem.responsecode && //Unaidded awareness showing Toatal as NA
              (row.Level1NestRowVariableCode == RowlNestItem.variablecode || this.isBrandAnalysis) &&
              row.Level2RowResponseCode == RowNestItemLevel2.responsecode &&
              row.Level2NestRowVariableCode == RowNestItemLevel2.variablecode &&
              row.colResponseCode == colItem.responsecode &&
              row.colVariableCode == colItem.variablecode &&
              row.rowChild == rowItem.Child &&
              row.colChild == colItem.Child &&
              row.Level1RowChild == RowlNestItem.Child &&
              row.Level2RowChild == RowNestItemLevel2.Child &&
              (row.customCalculationId == rowItem.customCalculationId ||
                row.customCalculationId == colItem.customCalculationId)
            );
          } else {
            return (
              row.rowResponseCode == rowItem.responsecode &&
              row.colResponseCode == colItem.responsecode &&
              //row.Level1RowResponseCode == RowlNestItem.responsecode && //Unaidded awareness showing Toatal as NA
              row.rowVariableCode == rowItem.variablecode &&
              row.colVariableCode == colItem.variablecode &&
              (row.Level1NestRowVariableCode == RowlNestItem.variablecode  || this.isBrandAnalysis) &&
              row.Level2RowResponseCode == RowNestItemLevel2.responsecode &&
              row.Level2NestRowVariableCode == RowNestItemLevel2.variablecode &&
              row.rowChild == rowItem.Child &&
              row.colChild == colItem.Child &&
              row.Level1RowChild == RowlNestItem.Child &&
              row.Level2RowChild == RowNestItemLevel2.Child
            );
          }
        }
      );
      return this.calculatePercentage(matchingRow);
    }
  }

  getPercentage_multiLevel(
    rowItem: any,
    RowlNestItem: any,
    colItem: any,
    colNestItem: any
  ): number | string {

    (window as any)['CrossTabData_Index'] = [];

    if (this.isTranspose) {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) =>
          row.isDerived && row.CalculationType.toLowerCase() == 'mean'
            ? row.colResponseCode == rowItem.responsecode &&
            row.colVariableCode == rowItem.variablecode &&
            row.Level1ColResponseCode == RowlNestItem.responsecode &&
            row.Level1NestColVariableCode == RowlNestItem.variablecode &&
            row.rowResponseCode == colItem.responsecode &&
            row.rowVariableCode == colItem.variablecode &&
            row.Level1RowResponseCode == colNestItem.responsecode &&
            row.Level1NestRowVariableCode == colNestItem.variablecode &&
            row.CalculationType.toLowerCase() == 'mean' &&
            row.colChild == rowItem.Child &&
            row.Level1ColChild == RowlNestItem.Child &&
            row.rowChild == colItem.Child &&
            row.Level1RowChild == colNestItem.Child
            : row.isCustomGroup
              ? row.colResponseCode == rowItem.responsecode &&
              row.colVariableCode == rowItem.variablecode &&
              row.Level1ColResponseCode == RowlNestItem.responsecode &&
              row.Level1NestColVariableCode == RowlNestItem.variablecode &&
              row.rowResponseCode == colItem.responsecode &&
              row.rowVariableCode == colItem.variablecode &&
              row.Level1RowResponseCode == colNestItem.responsecode &&
              row.Level1NestRowVariableCode == colNestItem.variablecode &&
              row.isCustomGroup == true &&
              (row.customGroupId == rowItem.customGroupId || row.customGroupId == colItem.customGroupId) &&
              row.colChild == rowItem.Child &&
              row.Level1ColChild == RowlNestItem.Child &&
              row.rowChild == colItem.Child &&
              row.Level1RowChild == colNestItem.Child
              : row.isCustomCalculation
                ? row.colResponseCode == rowItem.responsecode &&
                row.colVariableCode == rowItem.variablecode &&
                row.Level1ColResponseCode == RowlNestItem.responsecode &&
                row.Level1NestColVariableCode == RowlNestItem.variablecode &&
                row.rowResponseCode == colItem.responsecode &&
                row.rowVariableCode == colItem.variablecode &&
                row.Level1RowResponseCode == colNestItem.responsecode &&
                row.Level1NestRowVariableCode == colNestItem.variablecode &&
                row.isCustomCalculation == true &&
                (row.customCalculationId == rowItem.customCalculationId || row.customCalculationId == colItem.customCalculationId) &&
                row.colChild == rowItem.Child &&
                row.Level1ColChild == RowlNestItem.Child &&
                row.rowChild == colItem.Child &&
                row.Level1RowChild == colNestItem.Child
                : row.colResponseCode == rowItem.responsecode &&
                row.Level1ColResponseCode == RowlNestItem.responsecode &&
                row.rowResponseCode == colItem.responsecode &&
                row.Level1RowResponseCode == colNestItem.responsecode &&
                row.colVariableCode == rowItem.variablecode &&
                row.rowVariableCode == colItem.variablecode &&
                row.Level1NestColVariableCode == RowlNestItem.variablecode &&
                row.Level1NestRowVariableCode == colNestItem.variablecode &&
                row.colChild == rowItem.Child &&
                row.Level1ColChild == RowlNestItem.Child &&
                row.rowChild == colItem.Child &&
                row.Level1RowChild == colNestItem.Child
      );
      return this.formatColumnTotalSignificance(
        this.calculatePercentage(matchingRow),
        colNestItem
      );
    }
    else {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) =>
          row.isDerived && row.CalculationType.toLowerCase() == 'mean'
            ? row.rowResponseCode == rowItem.responsecode &&
            row.rowVariableCode == rowItem.variablecode &&
            row.Level1RowResponseCode == RowlNestItem.responsecode &&
            (RowlNestItem.Child =="Total" || row.Level1NestRowVariableCode == RowlNestItem.variablecode) &&
            row.colResponseCode == colItem.responsecode &&
            row.colVariableCode == colItem.variablecode &&
            row.Level1ColResponseCode == colNestItem.responsecode &&
            row.Level1NestColVariableCode == colNestItem.variablecode &&
            row.CalculationType.toLowerCase() == 'mean' && row.rowChild == rowItem.Child &&
            row.Level1RowChild == RowlNestItem.Child &&
            row.colChild == colItem.Child &&
            row.Level1ColChild == colNestItem.Child
            :
            row.isCustomGroup
              ? row.rowResponseCode == rowItem.responsecode &&
              row.rowVariableCode == rowItem.variablecode &&
              row.Level1RowResponseCode == RowlNestItem.responsecode &&
              (RowlNestItem.Child =="Total" || row.Level1NestRowVariableCode == RowlNestItem.variablecode) &&
              row.colResponseCode == colItem.responsecode &&
              row.colVariableCode == colItem.variablecode &&
              row.Level1ColResponseCode == colNestItem.responsecode &&
              row.Level1NestColVariableCode == colNestItem.variablecode &&
              row.isCustomGroup == true &&
              (row.customGroupId == rowItem.customGroupId || row.customGroupId == colItem.customGroupId) &&
              row.rowChild == rowItem.Child &&
              row.Level1RowChild == RowlNestItem.Child &&
              row.colChild == colItem.Child &&
              row.Level1ColChild == colNestItem.Child :

              row.isCustomCalculation
                ? row.rowResponseCode == rowItem.responsecode &&
                row.rowVariableCode == rowItem.variablecode &&
                row.Level1RowResponseCode == RowlNestItem.responsecode &&
                (RowlNestItem.Child =="Total" || row.Level1NestRowVariableCode == RowlNestItem.variablecode) &&
                row.colResponseCode == colItem.responsecode &&
                row.colVariableCode == colItem.variablecode &&
                row.Level1ColResponseCode == colNestItem.responsecode &&
                row.Level1NestColVariableCode == colNestItem.variablecode &&
                row.isCustomCalculation == true &&
                (row.customCalculationId == rowItem.customCalculationId || row.customCalculationId == colItem.customCalculationId) &&
                row.rowChild == rowItem.Child &&
                row.Level1RowChild == RowlNestItem.Child &&
                row.colChild == colItem.Child &&
                row.Level1ColChild == colNestItem.Child :

                row.rowResponseCode == rowItem.responsecode &&
                row.Level1RowResponseCode == RowlNestItem.responsecode &&
                row.colResponseCode == colItem.responsecode &&
                row.Level1ColResponseCode == colNestItem.responsecode &&
                row.rowVariableCode == rowItem.variablecode &&
                row.colVariableCode == colItem.variablecode &&
                (RowlNestItem.Child =="Total" || row.Level1NestRowVariableCode == RowlNestItem.variablecode) &&
                row.Level1NestColVariableCode == colNestItem.variablecode &&
                row.rowChild == rowItem.Child &&
                row.Level1RowChild == RowlNestItem.Child &&
                row.colChild == colItem.Child &&
                row.Level1ColChild == colNestItem.Child
      );
      return this.formatColumnTotalSignificance(
        this.calculatePercentage(matchingRow),
        colNestItem
      );
    }
  }

  getPercentage_multiLevel_level2(
    rowLevel0: any,
    rowLevel1: any,
    rowLevel2: any,
    colLevel0: any,
    colLevel1: any,
    colLevel2: any
  ): number | string {
    //Find the row that matches the response codes
    (window as any)['CrossTabData_Index'] = [];

    if (this.isTranspose) {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) =>
          row.isDerived && row.CalculationType.toLowerCase() == 'mean'
            ? row.rowResponseCode == colLevel0.responsecode &&
            row.Level1RowResponseCode == colLevel1.responsecode &&
            row.Level2RowResponseCode == colLevel2.responsecode &&
            row.colVariableCode == rowLevel0.variablecode &&
            row.Level1NestColVariableCode == rowLevel1.variablecode &&
            row.Level2NestColVariableCode == rowLevel2.variablecode &&
            row.rowVariableCode == colLevel0.variablecode &&
            row.Level1NestRowVariableCode == colLevel1.variablecode &&
            row.Level2NestRowVariableCode == colLevel2.variablecode &&
            row.CalculationType.toLowerCase() == 'mean'
            : row.colResponseCode == rowLevel0.responsecode &&
            row.Level1ColResponseCode == rowLevel1.responsecode &&
            row.Level2ColResponseCode == rowLevel2.responsecode &&
            row.rowResponseCode == colLevel0.responsecode &&
            row.Level1RowResponseCode == colLevel1.responsecode &&
            row.Level2RowResponseCode == colLevel2.responsecode &&
            row.colVariableCode == rowLevel0.variablecode &&
            row.Level1NestColVariableCode == rowLevel1.variablecode &&
            row.Level2NestColVariableCode == rowLevel2.variablecode &&
            row.rowVariableCode == colLevel0.variablecode &&
            row.Level1NestRowVariableCode == colLevel1.variablecode &&
            row.Level2NestRowVariableCode == colLevel2.variablecode
      );
      return this.formatColumnTotalSignificance(
        this.calculatePercentage(matchingRow),
        colLevel1,
        colLevel2
      );
    } else {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) =>
          row.isDerived && row.CalculationType.toLowerCase() == 'mean'
            ? row.rowResponseCode == rowLevel0.responsecode &&
            row.rowVariableCode == rowLevel0.variablecode &&
            row.Level1RowResponseCode == rowLevel1.responsecode &&
            row.Level1NestRowVariableCode == rowLevel1.variablecode &&
            row.Level2RowResponseCode == rowLevel2.responsecode &&
            row.Level2NestRowVariableCode == rowLevel2.variablecode &&
            row.colResponseCode == colLevel0.responsecode &&
            row.colVariableCode == colLevel0.variablecode &&
            row.Level1ColResponseCode == colLevel1.responsecode &&
            row.Level1NestColVariableCode == colLevel1.variablecode &&
            row.Level2ColResponseCode == colLevel2.responsecode &&
            row.Level2NestColVariableCode == colLevel2.variablecode &&
            row.CalculationType.toLowerCase() == 'mean' &&
            row.rowChild == rowLevel0.Child &&
            row.Level1RowChild == rowLevel1.Child &&
            row.Level2RowChild == rowLevel2.Child &&
            row.colChild == colLevel0.Child &&
            row.Level1ColChild == colLevel1.Child &&
            row.Level2ColChild == colLevel2.Child :
            row.isCustomGroup
              ? row.rowResponseCode == rowLevel0.responsecode &&
              row.rowVariableCode == rowLevel0.variablecode &&
              row.Level1RowResponseCode == rowLevel1.responsecode &&
              row.Level1NestRowVariableCode == rowLevel1.variablecode &&
              row.Level2RowResponseCode == rowLevel2.responsecode &&
              row.Level2NestRowVariableCode == rowLevel2.variablecode &&
              row.colResponseCode == colLevel0.responsecode &&
              row.colVariableCode == colLevel0.variablecode &&
              row.Level1ColResponseCode == colLevel1.responsecode &&
              row.Level1NestColVariableCode == colLevel1.variablecode &&
              row.Level2ColResponseCode == colLevel2.responsecode &&
              row.Level2NestColVariableCode == colLevel2.variablecode &&
              row.isCustomGroup == true && (row.customGroupId == rowLevel0.customGroupId || row.customGroupId == colLevel2.customGroupId) &&
              row.rowChild == rowLevel0.Child &&
              row.Level1RowChild == rowLevel1.Child &&
              row.Level2RowChild == rowLevel2.Child &&
              row.colChild == colLevel0.Child &&
              row.Level1ColChild == colLevel1.Child &&
              row.Level2ColChild == colLevel2.Child :
              row.isCustomCalculation ?
                row.rowResponseCode == rowLevel0.responsecode &&
                row.rowVariableCode == rowLevel0.variablecode &&
                row.Level1RowResponseCode == rowLevel1.responsecode &&
                row.Level1NestRowVariableCode == rowLevel1.variablecode &&
                row.Level2RowResponseCode == rowLevel2.responsecode &&
                row.Level2NestRowVariableCode == rowLevel2.variablecode &&
                row.colResponseCode == colLevel0.responsecode &&
                row.colVariableCode == colLevel0.variablecode &&
                row.Level1ColResponseCode == colLevel1.responsecode &&
                row.Level1NestColVariableCode == colLevel1.variablecode &&
                row.Level2ColResponseCode == colLevel2.responsecode &&
                row.Level2NestColVariableCode == colLevel2.variablecode &&
                row.isCustomCalculation == true && (row.customCalculationId == rowLevel0.customCalculationId || row.customCalculationId == colLevel2.customCalculationId) &&
                row.rowChild == rowLevel0.Child &&
                row.Level1RowChild == rowLevel1.Child &&
                row.Level2RowChild == rowLevel2.Child &&
                row.colChild == colLevel0.Child &&
                row.Level1ColChild == colLevel1.Child &&
                row.Level2ColChild == colLevel2.Child
                : row.rowResponseCode == rowLevel0.responsecode &&
                row.Level1RowResponseCode == rowLevel1.responsecode &&
                row.Level2RowResponseCode == rowLevel2.responsecode &&
                row.colResponseCode == colLevel0.responsecode &&
                row.Level1ColResponseCode == colLevel1.responsecode &&
                row.Level2ColResponseCode == colLevel2.responsecode &&
                row.rowVariableCode == rowLevel0.variablecode &&
                row.Level1NestRowVariableCode == rowLevel1.variablecode &&
                row.Level2NestRowVariableCode == rowLevel2.variablecode &&
                row.colVariableCode == colLevel0.variablecode &&
                row.Level1NestColVariableCode == colLevel1.variablecode &&
                row.Level2NestColVariableCode == colLevel2.variablecode &&
                row.rowChild == rowLevel0.Child &&
                row.Level1RowChild == rowLevel1.Child &&
                row.Level2RowChild == rowLevel2.Child &&
                row.colChild == colLevel0.Child &&
                row.Level1ColChild == colLevel1.Child &&
                row.Level2ColChild == colLevel2.Child
      );
      return this.formatColumnTotalSignificance(
        this.calculatePercentage(matchingRow),
        colLevel1,
        colLevel2
      );
    }
  }

  getPercentage_multiLevel_Row_Level1(
    rowLevel0: any,
    rowLevel1: any,
    rowLevel2: any,
    colLevel0: any,
    colLevel1: any
  ): number | string {
    //Find the row that matches the response codes

    (window as any)['CrossTabData_Index'] = [];

    if (this.isTranspose) {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) =>
          row.isDerived && row.CalculationType.toLowerCase() == 'mean'
            ? row.rowResponseCode == colLevel0.responsecode &&
            row.Level1RowResponseCode == colLevel1.responsecode &&
            row.colVariableCode == rowLevel0.variablecode &&
            row.Level1NestColVariableCode == rowLevel1.variablecode &&
            row.Level2NestColVariableCode == rowLevel2.variablecode &&
            row.rowVariableCode == colLevel0.variablecode &&
            row.Level1NestRowVariableCode == colLevel1.variablecode &&
            row.CalculationType.toLowerCase() == 'mean'
            : row.colResponseCode == rowLevel0.responsecode &&
            row.Level1ColResponseCode == rowLevel1.responsecode &&
            row.Level2ColResponseCode == rowLevel2.responsecode &&
            row.rowResponseCode == colLevel0.responsecode &&
            row.Level1RowResponseCode == colLevel1.responsecode &&
            row.colVariableCode == rowLevel0.variablecode &&
            row.Level1NestColVariableCode == rowLevel1.variablecode &&
            row.Level2NestColVariableCode == rowLevel2.variablecode &&
            row.rowVariableCode == colLevel0.variablecode &&
            row.Level1NestRowVariableCode == colLevel1.variablecode
      );
      return this.formatColumnTotalSignificance(
        this.calculatePercentage(matchingRow),
        colLevel1
      );
    } else {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) =>
          row.isDerived && row.CalculationType.toLowerCase() == 'mean'
            ? row.rowResponseCode == rowLevel0.responsecode &&
            row.rowVariableCode == rowLevel0.variablecode &&
            row.Level1RowResponseCode == rowLevel1.responsecode &&
            row.Level1NestRowVariableCode == rowLevel1.variablecode &&
            row.Level2RowResponseCode == rowLevel2.responsecode &&
            row.Level2NestRowVariableCode == rowLevel2.variablecode &&
            row.colResponseCode == colLevel0.responsecode &&
            row.colVariableCode == colLevel0.variablecode &&
            row.Level1ColResponseCode == colLevel1.responsecode &&
            row.Level1NestColVariableCode == colLevel1.variablecode &&
            row.CalculationType.toLowerCase() == 'mean' &&
            row.rowChild == rowLevel0.Child &&
            row.Level1RowChild == rowLevel1.Child &&
            row.Level2RowChild == rowLevel2.Child &&
            row.colChild == colLevel0.Child &&
            row.Level1ColChild == colLevel1.Child
            : row.isCustomGroup
              ? row.rowResponseCode == rowLevel0.responsecode &&
              row.rowVariableCode == rowLevel0.variablecode &&
              row.Level1RowResponseCode == rowLevel1.responsecode &&
              row.Level1NestRowVariableCode == rowLevel1.variablecode &&
              row.Level2RowResponseCode == rowLevel2.responsecode &&
              row.Level2NestRowVariableCode == rowLevel2.variablecode &&
              row.colResponseCode == colLevel0.responsecode &&
              row.colVariableCode == colLevel0.variablecode &&
              row.Level1ColResponseCode == colLevel1.responsecode &&
              row.Level1NestColVariableCode == colLevel1.variablecode &&
              row.isCustomGroup == true &&
              (row.customGroupId == rowLevel0.customGroupId ||
                row.customGroupId == colLevel1.customGroupId) &&
              row.rowChild == rowLevel0.Child &&
              row.Level1RowChild == rowLevel1.Child &&
              row.Level2RowChild == rowLevel2.Child &&
              row.colChild == colLevel0.Child &&
              row.Level1ColChild == colLevel1.Child
              : row.isCustomCalculation
                ? row.rowResponseCode == rowLevel0.responsecode &&
                row.rowVariableCode == rowLevel0.variablecode &&
                row.Level1RowResponseCode == rowLevel1.responsecode &&
                row.Level1NestRowVariableCode == rowLevel1.variablecode &&
                row.Level2RowResponseCode == rowLevel2.responsecode &&
                row.Level2NestRowVariableCode == rowLevel2.variablecode &&
                row.colResponseCode == colLevel0.responsecode &&
                row.colVariableCode == colLevel0.variablecode &&
                row.Level1ColResponseCode == colLevel1.responsecode &&
                row.Level1NestColVariableCode == colLevel1.variablecode &&
                row.isCustomCalculation == true &&
                (row.customCalculationId == rowLevel0.customCalculationId ||
                  row.customCalculationId == colLevel1.customCalculationId) &&
                row.rowChild == rowLevel0.Child &&
                row.Level1RowChild == rowLevel1.Child &&
                row.Level2RowChild == rowLevel2.Child &&
                row.colChild == colLevel0.Child &&
                row.Level1ColChild == colLevel1.Child
                :
                row.rowResponseCode == rowLevel0.responsecode &&
                row.Level1RowResponseCode == rowLevel1.responsecode &&
                row.Level2RowResponseCode == rowLevel2.responsecode &&
                row.colResponseCode == colLevel0.responsecode &&
                row.Level1ColResponseCode == colLevel1.responsecode &&
                row.rowVariableCode == rowLevel0.variablecode &&
                row.Level1NestRowVariableCode == rowLevel1.variablecode &&
                row.Level2NestRowVariableCode == rowLevel2.variablecode &&
                row.colVariableCode == colLevel0.variablecode &&
                row.Level1NestColVariableCode == colLevel1.variablecode &&
                row.rowChild == rowLevel0.Child &&
                row.Level1RowChild == rowLevel1.Child &&
                row.Level2RowChild == rowLevel2.Child &&
                row.colChild == colLevel0.Child &&
                row.Level1ColChild == colLevel1.Child
      );
      return this.formatColumnTotalSignificance(
        this.calculatePercentage(matchingRow),
        colLevel1
      );
    }
  }


  getPercentage_multiLevel_rowLevel1_columnLevel2(
    rowLevel0: any,
    rowLevel1: any,
    colLevel0: any,
    colLevel1: any,
    colLevel2: any
  ): number | string {
    //Find the row that matches the response codes
    (window as any)['CrossTabData_Index'] = [];

    if (this.isTranspose) {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) =>
          row.isDerived && row.CalculationType.toLowerCase() == 'mean'
            ?
            row.colResponseCode == rowLevel0.responsecode &&
            row.colVariableCode == rowLevel0.variablecode &&
            row.rowVariableCode == colLevel0.variablecode &&
            row.rowResponseCode == colLevel0.responsecode &&
            row.Level1ColResponseCode == rowLevel1.responsecode &&
            row.Level1NestColVariableCode == rowLevel1.variablecode &&
            row.Level1RowResponseCode == colLevel1.responsecode &&
            row.Level1NestRowVariableCode == colLevel1.variablecode &&
            row.Level2RowResponseCode == colLevel2.responsecode &&
            row.Level2NestRowVariableCode == colLevel2.variablecode &&
            row.CalculationType.toLowerCase() == 'mean'
            : row.colResponseCode == rowLevel0.responsecode &&
            row.colVariableCode == rowLevel0.variablecode &&
            row.Level1ColResponseCode == rowLevel1.responsecode &&
            row.Level1NestColVariableCode == rowLevel1.variablecode &&
            row.rowVariableCode == colLevel0.variablecode &&
            row.rowResponseCode == colLevel0.responsecode &&
            row.Level1RowResponseCode == colLevel1.responsecode &&
            row.Level1NestRowVariableCode == colLevel1.variablecode &&
            row.Level2RowResponseCode == colLevel2.responsecode &&
            row.Level2NestRowVariableCode == colLevel2.variablecode
      );
      return this.formatColumnTotalSignificance(
        this.calculatePercentage(matchingRow),
        colLevel1,
        colLevel2
      );
    } else {
      const matchingRow = this.crossTabReceivedData.find(
        (row: CrossTabOutputItem) =>
          row.isDerived && row.CalculationType.toLowerCase() == 'mean'
            ? row.rowResponseCode == rowLevel0.responsecode &&
            row.rowVariableCode == rowLevel0.variablecode &&
            row.colVariableCode == colLevel0.variablecode &&
            row.colResponseCode == colLevel0.responsecode &&
            row.Level1RowResponseCode == rowLevel1.responsecode &&
            (rowLevel1.Child =="Total" || row.Level1NestRowVariableCode == rowLevel1.variablecode) &&
            row.Level1ColResponseCode == colLevel1.responsecode &&
            row.Level1NestColVariableCode == colLevel1.variablecode &&
            row.Level2ColResponseCode == colLevel2.responsecode &&
            row.Level2NestColVariableCode == colLevel2.variablecode &&
            row.CalculationType.toLowerCase() == 'mean' &&
            row.rowChild == rowLevel0.Child &&
            row.Level1RowChild == rowLevel1.Child &&
            row.colChild == colLevel0.Child &&
            row.Level1ColChild == colLevel1.Child &&
            row.Level2ColChild == colLevel2.Child
            : row.isCustomGroup
              ? row.rowResponseCode == rowLevel0.responsecode &&
              row.rowVariableCode == rowLevel0.variablecode &&
              row.colVariableCode == colLevel0.variablecode &&
              row.colResponseCode == colLevel0.responsecode &&
              row.Level1RowResponseCode == rowLevel1.responsecode &&
              (rowLevel1.Child =="Total" || row.Level1NestRowVariableCode == rowLevel1.variablecode) &&
              row.Level1ColResponseCode == colLevel1.responsecode &&
              row.Level1NestColVariableCode == colLevel1.variablecode &&
              row.Level2ColResponseCode == colLevel2.responsecode &&
              row.Level2NestColVariableCode == colLevel2.variablecode &&
              row.isCustomGroup == true &&
              (row.customGroupId == rowLevel0.customGroupId ||
                row.customGroupId == colLevel2.customGroupId) &&
              row.rowChild == rowLevel0.Child &&
              row.Level1RowChild == rowLevel1.Child &&
              row.colChild == colLevel0.Child &&
              row.Level1ColChild == colLevel1.Child &&
              row.Level2ColChild == colLevel2.Child
              : row.isCustomCalculation
                ? row.rowResponseCode == rowLevel0.responsecode &&
                row.rowVariableCode == rowLevel0.variablecode &&
                row.colVariableCode == colLevel0.variablecode &&
                row.colResponseCode == colLevel0.responsecode &&
                row.Level1RowResponseCode == rowLevel1.responsecode &&
                (rowLevel1.Child =="Total" || row.Level1NestRowVariableCode == rowLevel1.variablecode) &&
                row.Level1ColResponseCode == colLevel1.responsecode &&
                row.Level1NestColVariableCode == colLevel1.variablecode &&
                row.Level2ColResponseCode == colLevel2.responsecode &&
                row.Level2NestColVariableCode == colLevel2.variablecode &&
                row.isCustomCalculation == true &&
                (row.customCalculationId == rowLevel0.customCalculationId ||
                  row.customCalculationId == colLevel2.customCalculationId) &&
                row.rowChild == rowLevel0.Child &&
                row.Level1RowChild == rowLevel1.Child &&
                row.colChild == colLevel0.Child &&
                row.Level1ColChild == colLevel1.Child &&
                row.Level2ColChild == colLevel2.Child
                : row.rowResponseCode == rowLevel0.responsecode &&
                row.rowVariableCode == rowLevel0.variablecode &&
                row.Level1RowResponseCode == rowLevel1.responsecode &&
                (rowLevel1.Child =="Total" || row.Level1NestRowVariableCode == rowLevel1.variablecode) &&
                row.colVariableCode == colLevel0.variablecode &&
                row.colResponseCode == colLevel0.responsecode &&
                row.Level1ColResponseCode == colLevel1.responsecode &&
                row.Level1NestColVariableCode == colLevel1.variablecode &&
                row.Level2ColResponseCode == colLevel2.responsecode &&
                row.Level2NestColVariableCode == colLevel2.variablecode &&
                row.rowChild == rowLevel0.Child &&
                row.Level1RowChild == rowLevel1.Child &&
                row.colChild == colLevel0.Child &&
                row.Level1ColChild == colLevel1.Child &&
                row.Level2ColChild == colLevel2.Child
      );
      return this.formatColumnTotalSignificance(
        this.calculatePercentage(matchingRow),
        colLevel1,
        colLevel2
      );
    }
  }

  pairWiseText(pairWiseValues: PairWiseDetails[]) {
    let html = "";
    if (pairWiseValues != null && pairWiseValues.length > 0) {
      html = " | ";
      pairWiseValues.forEach((x: PairWiseDetails) => {
        html = ` ${html} <span class='${x.color}'>${x.pairWiseAlph}</span>`
      })
    }
    return html;
  }

  customRound(num: number): number {
    const roundedValue = Math.round(num * 10) / 10; // Round to one decimal place
    return roundedValue;
  }
}
